export type RouteOwnerType = "PLATFORM" | "WORKSPACE" | "PRODUCT" | "MODULE" | "ADMINISTRATION" | "ROADMAP";

export type RouteStatus = "ACTIVE" | "HIDDEN" | "PLACEHOLDER" | "ROADMAP" | "DEPRECATED";

export interface RouteOwnershipEntry {
  route: string;
  pageName: string;
  ownerType: RouteOwnerType;
  ownerName: string;
  platformName: string;
  workspaceName: string | null;
  moduleName: string;
  parentRoute: string | null;
  returnRoute: string;
  navigationLabel: string;
  visibility: RouteStatus;
  status: RouteStatus;
}

export interface RouteInventorySummary {
  totalActiveRoutes: number;
  ownerTypeCounts: Record<RouteOwnerType, number>;
  statusCounts: Record<RouteStatus, number>;
  roadmapSlots: number;
}

const PUBLIC_FORM_ROUTES = [
  "/",
  "/event-inquiry",
  "/rsvp-hub",
  "/memory-hub",
  "/archive-hub",
  "/memory-media-vault",
  "/signature-song",
  "/signature-highlight-reel",
  "/partner-referral",
  "/proposal/:token",
  "/agreement/:token",
  "/payment/:token",
  "/onboarding/:token",
  "/upload-link/:uploadSlug",
  "/upload/:uploadSlug",
  "/upload/:clientSlug/:eventSlug",
  "/gallery/:clientSlug/:eventSlug",
  "/memory-hub/:clientSlug/:eventSlug",
] as const;

const AUTH_ROUTES = ["/login", "/admin/login"] as const;

const CLIENT_PORTAL_ROUTES = [
  "/client/portal",
  "/client/dashboard",
  "/client/memory-hub/vault",
  "/client/pending",
  "/client/approved",
  "/client/featured",
  "/client/hidden",
  "/client/not-approved",
  "/client/upload-link",
  "/client/storage-billing",
  "/client/settings",
  "/client/instructions",
] as const;

const BUSINESS_CENTER_OS_ROUTES = [
  "/admin/business-center-os",
  "/admin/executive-dashboard",
  "/admin/leads",
  "/admin/leads/:leadId",
  "/admin/client-onboarding-center",
  "/admin/universal-user-management",
  "/admin/workspace-provisioning-center",
  "/admin/white-label-branding-center",
  "/admin/automation-engine",
  "/admin/operational-registry",
  "/admin/agreements",
  "/admin/agreement-records",
  "/admin/agreement-records/:agreementId",
  "/admin/payments",
  "/admin/payment-records",
  "/admin/payment-records/:paymentId",
  "/admin/service-agreements",
] as const;

const TRUEVANCE_WORKSPACE_ROUTES = [
  "/admin/truevance-business-center",
  "/admin/truevance-connect",
  "/admin/truevance-sales-center",
  "/admin/truevance-assessment-center",
  "/admin/truevance-proposal-center",
  "/admin/truevance-proposal-center/:proposalId",
  "/admin/truevance-client-center",
  "/admin/truevance-company-users",
  "/admin/truevance-project-center",
  "/admin/truevance-project-center/:projectId",
  "/admin/truevance-tasks",
  "/admin/truevance-document-center",
  "/admin/truevance-roles-center",
  "/admin/truevance-communication-center",
  "/admin/truevance-analytics-center",
  "/admin/truevance-settings-center",
  "/admin/consulting-pipeline",
  "/admin/assessment-framework",
  "/admin/client-engagement-dashboard",
  "/admin/service-categories",
  "/admin/platform-architecture",
  "/admin/truevance-opportunities",
  "/admin/truevance-opportunities/:opportunityId",
  "/admin/truevance-assessments",
  "/admin/truevance-assessments/:assessmentId",
  "/admin/truevance-projects",
  "/admin/truevance-projects/:projectId",
  "/admin/consulting-assessments",
  "/admin/consulting-assessments/:assessmentId",
  "/admin/truevance-recommendations",
  "/admin/truevance-recommendations/:recommendationId",
  "/admin/implementation-roadmaps",
  "/admin/implementation-roadmaps/:roadmapId",
] as const;

const KREATETHEDATE_ROUTES = [
  "/admin/forms-directory",
  "/admin/journey-dashboard",
  "/admin/proposal-dashboard",
  "/admin/payment-settings",
  "/admin/scheduler",
  "/admin/scheduler-settings",
  "/admin/scheduling",
  "/admin/welcome-email-settings",
  "/admin/onboarding-settings",
  "/admin/discovery-call-notes",
  "/admin/kickoff-call-prep",
  "/admin/client-directory",
  "/admin/project-dashboard",
  "/admin/partner-referral-tracker",
  "/admin/client-feedback",
  "/admin/general-support-request",
  "/admin/forms-client-directory",
  "/admin/forms-client/:id",
  "/admin/proposals",
] as const;

const MEMORY_MEDIA_VAULT_ROUTES = [
  "/admin/dashboard",
  "/admin/clients",
  "/admin/client/:clientId",
  "/admin/events",
  "/admin/media",
  "/admin/storage-setup",
  "/admin/activity-log",
  "/admin/settings",
  "/admin/memory-hub/vault",
  "/demo/memory-media-vault",
  "/demo/memory-media-vault/upload",
  "/demo/memory-media-vault/gallery",
] as const;

const AR_RECOVERY_ROUTES = [
  "/admin/ar-recovery",
  "/admin/ar-recovery/customer-accounts",
  "/admin/ar-recovery/aging-management",
  "/admin/ar-recovery/promise-to-pay",
  "/admin/ar-recovery/smart-tickler",
  "/admin/ar-recovery/collection-letters",
  "/admin/ar-recovery/document-center",
  "/admin/ar-recovery/statements",
  "/admin/ar-recovery/recovery-activity",
  "/admin/ar-recovery/recovery-reporting",
  "/admin/ar-recovery/recovery-analytics",
  "/admin/ar-recovery/manager-command-center",
  "/admin/ar-recovery/client-portal",
  "/admin/ar-recovery/white-label-settings",
  "/admin/ar-recovery/commercialization-center",
  "/admin/ar-recovery/enterprise-readiness",
] as const;

export const ACTIVE_ROUTE_PATHS = [
  ...PUBLIC_FORM_ROUTES,
  ...AUTH_ROUTES,
  ...CLIENT_PORTAL_ROUTES,
  ...BUSINESS_CENTER_OS_ROUTES,
  ...TRUEVANCE_WORKSPACE_ROUTES,
  ...KREATETHEDATE_ROUTES,
  ...MEMORY_MEDIA_VAULT_ROUTES,
  ...AR_RECOVERY_ROUTES,
] as const;

export const ROADMAP_ROUTE_SLOTS = [
  { pageName: "Marketplace", ownerType: "ROADMAP" as const },
  { pageName: "AI Center", ownerType: "ROADMAP" as const },
  { pageName: "Template Library", ownerType: "ROADMAP" as const },
  { pageName: "Workflow Builder", ownerType: "ROADMAP" as const },
] as const;

const PAGE_NAME_OVERRIDES: Record<string, string> = {
  "/": "Forms Portal Home",
  "/login": "Login",
  "/admin/login": "Admin Login",
  "/event-inquiry": "Event Inquiry",
  "/rsvp-hub": "RSVP Hub",
  "/memory-hub": "Memory Hub",
  "/archive-hub": "Archive Hub",
  "/memory-media-vault": "Memory Media Vault",
  "/signature-song": "Signature Song",
  "/signature-highlight-reel": "Signature Highlight Reel",
  "/partner-referral": "Partner Referral",
  "/proposal/:token": "Proposal Acceptance",
  "/agreement/:token": "Agreement Signature",
  "/payment/:token": "Payment",
  "/onboarding/:token": "Onboarding",
  "/upload-link/:uploadSlug": "Upload Link",
  "/upload/:uploadSlug": "Upload",
  "/upload/:clientSlug/:eventSlug": "Upload",
  "/gallery/:clientSlug/:eventSlug": "Gallery",
  "/memory-hub/:clientSlug/:eventSlug": "Memory Hub Experience",
  "/client/portal": "Universal Client Portal",
  "/client/dashboard": "Client Dashboard",
  "/client/memory-hub/vault": "Memory Media Vault",
  "/client/pending": "Pending Media",
  "/client/approved": "Approved Media",
  "/client/featured": "Featured Media",
  "/client/hidden": "Hidden Media",
  "/client/not-approved": "Not Approved Media",
  "/client/upload-link": "Upload Link",
  "/client/storage-billing": "Storage Billing",
  "/client/settings": "Client Settings",
  "/client/instructions": "Instructions",
  "/admin/business-center-os": "Business Center OS Home",
  "/admin/executive-dashboard": "Executive Dashboard",
  "/admin/leads": "Sales & Lead Center",
  "/admin/leads/:leadId": "Lead Detail",
  "/admin/client-onboarding-center": "Client Onboarding Center",
  "/admin/universal-user-management": "Universal User Management",
  "/admin/workspace-provisioning-center": "Workspace Provisioning Center",
  "/admin/white-label-branding-center": "White Label Branding Center",
  "/admin/automation-engine": "Automation Engine",
  "/admin/operational-registry": "Operational Registry Inspector",
  "/admin/agreements": "Agreements",
  "/admin/agreement-records": "Agreement Records",
  "/admin/agreement-records/:agreementId": "Agreement Detail",
  "/admin/payments": "Payments",
  "/admin/payment-records": "Payment Records",
  "/admin/payment-records/:paymentId": "Payment Detail",
  "/admin/service-agreements": "Service Agreements",
  "/admin/truevance-business-center": "Truevance Business Center",
  "/admin/truevance-connect": "Truevance Connect",
  "/admin/truevance-sales-center": "Sales & Lead Center",
  "/admin/truevance-assessment-center": "Assessment Center",
  "/admin/truevance-proposal-center": "Proposal Center",
  "/admin/truevance-proposal-center/:proposalId": "Proposal Detail",
  "/admin/truevance-client-center": "Client Center",
  "/admin/truevance-company-users": "Company Users",
  "/admin/truevance-project-center": "Project Center",
  "/admin/truevance-project-center/:projectId": "Project Detail",
  "/admin/truevance-tasks": "Tasks Center",
  "/admin/truevance-document-center": "Document Center",
  "/admin/truevance-roles-center": "Roles & Permissions",
  "/admin/truevance-communication-center": "Communication Center",
  "/admin/truevance-analytics-center": "Analytics Center",
  "/admin/truevance-settings-center": "Settings Center",
  "/admin/consulting-pipeline": "Consulting Pipeline",
  "/admin/assessment-framework": "Assessment Framework",
  "/admin/client-engagement-dashboard": "Client Engagement Dashboard",
  "/admin/service-categories": "Service Categories",
  "/admin/platform-architecture": "Platform Architecture",
  "/admin/truevance-opportunities": "Opportunities",
  "/admin/truevance-opportunities/:opportunityId": "Opportunity Detail",
  "/admin/truevance-assessments": "Assessments",
  "/admin/truevance-assessments/:assessmentId": "Assessment Detail",
  "/admin/truevance-projects": "Projects",
  "/admin/truevance-projects/:projectId": "Project Detail",
  "/admin/consulting-assessments": "Consulting Assessments",
  "/admin/consulting-assessments/:assessmentId": "Consulting Assessment Detail",
  "/admin/truevance-recommendations": "Recommendations",
  "/admin/truevance-recommendations/:recommendationId": "Recommendation Detail",
  "/admin/implementation-roadmaps": "Implementation Roadmaps",
  "/admin/implementation-roadmaps/:roadmapId": "Roadmap Detail",
  "/admin/forms-directory": "Forms Directory",
  "/admin/journey-dashboard": "Journey Dashboard",
  "/admin/proposal-dashboard": "Proposal Dashboard",
  "/admin/payment-settings": "Payment Settings",
  "/admin/scheduler": "Scheduler",
  "/admin/scheduler-settings": "Scheduler Settings",
  "/admin/scheduling": "Scheduling",
  "/admin/welcome-email-settings": "Welcome Email Settings",
  "/admin/onboarding-settings": "Onboarding Settings",
  "/admin/discovery-call-notes": "Discovery Call Notes",
  "/admin/kickoff-call-prep": "Kickoff Call Prep",
  "/admin/client-directory": "Client Directory",
  "/admin/project-dashboard": "Project Dashboard",
  "/admin/partner-referral-tracker": "Partner Referral Tracker",
  "/admin/client-feedback": "Client Feedback",
  "/admin/general-support-request": "General Support Request",
  "/admin/forms-client-directory": "Forms Client Directory",
  "/admin/forms-client/:id": "Forms Client Profile",
  "/admin/proposals": "Proposals",
  "/admin/dashboard": "Admin Dashboard",
  "/admin/clients": "Clients",
  "/admin/client/:clientId": "Client Detail",
  "/admin/events": "Events",
  "/admin/media": "Media",
  "/admin/storage-setup": "Storage Setup",
  "/admin/activity-log": "Activity Log",
  "/admin/settings": "Settings",
  "/admin/memory-hub/vault": "Memory Vault",
  "/demo/memory-media-vault": "Memory Media Vault Demo",
  "/demo/memory-media-vault/upload": "Memory Media Vault Demo Upload",
  "/demo/memory-media-vault/gallery": "Memory Media Vault Demo Gallery",
  "/admin/ar-recovery": "Recovery Dashboard",
  "/admin/ar-recovery/customer-accounts": "Customer Accounts",
  "/admin/ar-recovery/aging-management": "Aging Management",
  "/admin/ar-recovery/promise-to-pay": "Promise To Pay",
  "/admin/ar-recovery/smart-tickler": "Smart Tickler",
  "/admin/ar-recovery/collection-letters": "Collection Letters",
  "/admin/ar-recovery/document-center": "Document Center",
  "/admin/ar-recovery/statements": "Statements",
  "/admin/ar-recovery/recovery-activity": "Recovery Activity",
  "/admin/ar-recovery/recovery-reporting": "Recovery Reporting",
  "/admin/ar-recovery/recovery-analytics": "Recovery Analytics",
  "/admin/ar-recovery/manager-command-center": "Manager Command Center",
  "/admin/ar-recovery/client-portal": "Client Portal Foundation",
  "/admin/ar-recovery/white-label-settings": "White Label Settings",
  "/admin/ar-recovery/commercialization-center": "Business Platform",
  "/admin/ar-recovery/enterprise-readiness": "Business Platform",
};

const SHARED_BUSINESS_CENTER_MODULES = new Set([
  "/admin/business-center-os",
  "/admin/executive-dashboard",
  "/admin/leads",
  "/admin/leads/:leadId",
  "/admin/client-onboarding-center",
  "/admin/universal-user-management",
  "/admin/workspace-provisioning-center",
  "/admin/white-label-branding-center",
  "/admin/automation-engine",
  "/admin/operational-registry",
  "/admin/agreements",
  "/admin/agreement-records",
  "/admin/agreement-records/:agreementId",
  "/admin/payments",
  "/admin/payment-records",
  "/admin/payment-records/:paymentId",
  "/admin/service-agreements",
  "/admin/truevance-client-center",
  "/admin/truevance-company-users",
  "/admin/truevance-project-center",
  "/admin/truevance-project-center/:projectId",
  "/admin/truevance-tasks",
  "/admin/truevance-document-center",
  "/admin/truevance-roles-center",
] as const);

const TRUEVANCE_WORKSPACE_MODULES = new Set([
  "/admin/truevance-business-center",
  "/admin/truevance-connect",
  "/admin/truevance-sales-center",
  "/admin/truevance-assessment-center",
  "/admin/truevance-proposal-center",
  "/admin/truevance-proposal-center/:proposalId",
  "/admin/truevance-communication-center",
  "/admin/truevance-analytics-center",
  "/admin/truevance-settings-center",
  "/admin/consulting-pipeline",
  "/admin/assessment-framework",
  "/admin/client-engagement-dashboard",
  "/admin/service-categories",
  "/admin/platform-architecture",
  "/admin/truevance-opportunities",
  "/admin/truevance-opportunities/:opportunityId",
  "/admin/truevance-assessments",
  "/admin/truevance-assessments/:assessmentId",
  "/admin/truevance-projects",
  "/admin/truevance-projects/:projectId",
  "/admin/consulting-assessments",
  "/admin/consulting-assessments/:assessmentId",
  "/admin/truevance-recommendations",
  "/admin/truevance-recommendations/:recommendationId",
  "/admin/implementation-roadmaps",
  "/admin/implementation-roadmaps/:roadmapId",
] as const);

const KREATETHEDATE_WORKSPACE_MODULES = new Set([
  "/admin/forms-directory",
  "/admin/journey-dashboard",
  "/admin/proposal-dashboard",
  "/admin/payment-settings",
  "/admin/scheduler",
  "/admin/scheduler-settings",
  "/admin/scheduling",
  "/admin/welcome-email-settings",
  "/admin/onboarding-settings",
  "/admin/discovery-call-notes",
  "/admin/kickoff-call-prep",
  "/admin/client-directory",
  "/admin/project-dashboard",
  "/admin/partner-referral-tracker",
  "/admin/client-feedback",
  "/admin/general-support-request",
  "/admin/forms-client-directory",
  "/admin/forms-client/:id",
  "/admin/proposals",
] as const);

const MEMORY_MEDIA_VAULT_PRODUCT_ROUTES = new Set([
  "/client/portal",
  "/client/dashboard",
  "/client/memory-hub/vault",
  "/client/pending",
  "/client/approved",
  "/client/featured",
  "/client/hidden",
  "/client/not-approved",
  "/client/upload-link",
  "/client/storage-billing",
  "/client/settings",
  "/client/instructions",
  "/admin/dashboard",
  "/admin/clients",
  "/admin/client/:clientId",
  "/admin/events",
  "/admin/media",
  "/admin/storage-setup",
  "/admin/activity-log",
  "/admin/settings",
  "/admin/memory-hub/vault",
  "/demo/memory-media-vault",
  "/demo/memory-media-vault/upload",
  "/demo/memory-media-vault/gallery",
] as const);

const AR_RECOVERY_PRODUCT_ROUTES = new Set([
  "/admin/ar-recovery",
  "/admin/ar-recovery/customer-accounts",
  "/admin/ar-recovery/aging-management",
  "/admin/ar-recovery/promise-to-pay",
  "/admin/ar-recovery/smart-tickler",
  "/admin/ar-recovery/collection-letters",
  "/admin/ar-recovery/document-center",
  "/admin/ar-recovery/statements",
  "/admin/ar-recovery/recovery-activity",
  "/admin/ar-recovery/recovery-reporting",
  "/admin/ar-recovery/recovery-analytics",
  "/admin/ar-recovery/manager-command-center",
  "/admin/ar-recovery/client-portal",
  "/admin/ar-recovery/white-label-settings",
  "/admin/ar-recovery/commercialization-center",
  "/admin/ar-recovery/enterprise-readiness",
] as const);

const ADMINISTRATION_ROUTES: ReadonlySet<string> = new Set(["/login", "/admin/login"]);

function titleize(value: string) {
  return value
    .split(/[-/]/g)
    .filter(Boolean)
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(" ");
}

function normalizePath(pathname: string) {
  if (pathname.startsWith("/admin/truevance-proposal-center/")) return "/admin/truevance-proposal-center/:proposalId";
  if (pathname.startsWith("/admin/truevance-project-center/")) return "/admin/truevance-project-center/:projectId";
  if (pathname.startsWith("/admin/truevance-opportunities/")) return "/admin/truevance-opportunities/:opportunityId";
  if (pathname.startsWith("/admin/truevance-assessments/")) return "/admin/truevance-assessments/:assessmentId";
  if (pathname.startsWith("/admin/truevance-projects/")) return "/admin/truevance-projects/:projectId";
  if (pathname.startsWith("/admin/consulting-assessments/")) return "/admin/consulting-assessments/:assessmentId";
  if (pathname.startsWith("/admin/truevance-recommendations/")) return "/admin/truevance-recommendations/:recommendationId";
  if (pathname.startsWith("/admin/implementation-roadmaps/")) return "/admin/implementation-roadmaps/:roadmapId";
  if (pathname.startsWith("/admin/leads/")) return "/admin/leads/:leadId";
  if (pathname.startsWith("/admin/agreement-records/")) return "/admin/agreement-records/:agreementId";
  if (pathname.startsWith("/admin/payment-records/")) return "/admin/payment-records/:paymentId";
  if (pathname.startsWith("/admin/forms-client/")) return "/admin/forms-client/:id";
  if (pathname.startsWith("/admin/client/")) return "/admin/client/:clientId";
  if (pathname.startsWith("/proposal/")) return "/proposal/:token";
  if (pathname.startsWith("/agreement/")) return "/agreement/:token";
  if (pathname.startsWith("/payment/")) return "/payment/:token";
  if (pathname.startsWith("/onboarding/")) return "/onboarding/:token";
  if (pathname.startsWith("/upload-link/")) return "/upload-link/:uploadSlug";
  if (pathname.startsWith("/upload/")) {
    const segments = pathname.split("/").filter(Boolean);
    return segments.length === 3 ? "/upload/:uploadSlug" : "/upload/:clientSlug/:eventSlug";
  }
  if (pathname.startsWith("/gallery/")) return "/gallery/:clientSlug/:eventSlug";
  if (pathname.startsWith("/memory-hub/") && pathname !== "/memory-hub") return "/memory-hub/:clientSlug/:eventSlug";
  return pathname;
}

function isAny(pathname: string, candidates: ReadonlySet<string>) {
  return candidates.has(pathname);
}

function buildOwnership(pathname: string): Omit<RouteOwnershipEntry, "route" | "pageName" | "moduleName"> {
  const route = normalizePath(pathname);

  if (ADMINISTRATION_ROUTES.has(route)) {
    return {
      ownerType: "ADMINISTRATION",
      ownerName: "Business Center OS",
      platformName: "Business Center OS",
      workspaceName: null,
      parentRoute: "/login",
      returnRoute: "/login",
      navigationLabel: "Admin Home",
      visibility: "ACTIVE",
      status: "ACTIVE",
    };
  }

  if (route === "/admin/business-center-os") {
    return {
      ownerType: "PLATFORM",
      ownerName: "Business Center OS",
      platformName: "Business Center OS",
      workspaceName: null,
      parentRoute: null,
      returnRoute: "/admin/business-center-os",
      navigationLabel: "Business Center OS Home",
      visibility: "ACTIVE",
      status: "ACTIVE",
    };
  }

  if (isAny(route, SHARED_BUSINESS_CENTER_MODULES)) {
    return {
      ownerType: route.startsWith("/admin/truevance-") ? "MODULE" : "MODULE",
      ownerName: "Business Center OS",
      platformName: "Business Center OS",
      workspaceName: route.startsWith("/admin/truevance-") ? "Truevance Connect" : null,
      parentRoute: "/admin/business-center-os",
      returnRoute: "/admin/business-center-os",
      navigationLabel: "Business Center OS Home",
      visibility: "ACTIVE",
      status: "ACTIVE",
    };
  }

  if (isAny(route, TRUEVANCE_WORKSPACE_MODULES)) {
    return {
      ownerType: route === "/admin/truevance-business-center" || route === "/admin/truevance-connect" ? "WORKSPACE" : "WORKSPACE",
      ownerName: "Truevance Connect",
      platformName: "Business Center OS",
      workspaceName: "Truevance Connect",
      parentRoute: "/admin/truevance-business-center",
      returnRoute: "/admin/truevance-business-center",
      navigationLabel: "Workspace Home",
      visibility: "ACTIVE",
      status: "ACTIVE",
    };
  }

  if (route === "/" || isAny(route, KREATETHEDATE_WORKSPACE_MODULES)) {
    return {
      ownerType: route === "/" ? "WORKSPACE" : "WORKSPACE",
      ownerName: "Kreatethedate",
      platformName: "Kreatethedate",
      workspaceName: null,
      parentRoute: "/admin/forms-directory",
      returnRoute: "/admin/forms-directory",
      navigationLabel: "Workspace Home",
      visibility: "ACTIVE",
      status: "ACTIVE",
    };
  }

  if (route === "/event-inquiry" || isPublicKreatethedateProduct(route)) {
    return {
      ownerType: "PRODUCT",
      ownerName: "Kreatethedate",
      platformName: "Kreatethedate",
      workspaceName: null,
      parentRoute: "/",
      returnRoute: "/",
      navigationLabel: "Product Home",
      visibility: "ACTIVE",
      status: "ACTIVE",
    };
  }

  if (isAny(route, MEMORY_MEDIA_VAULT_PRODUCT_ROUTES)) {
    const isAdminSurface = route.startsWith("/admin/");
    const isClientSurface = route.startsWith("/client/");
    return {
      ownerType: "PRODUCT",
      ownerName: "Memory Media Vault",
      platformName: "Memory Media Vault",
      workspaceName: null,
      parentRoute: isAdminSurface ? "/admin/dashboard" : isClientSurface ? "/client/dashboard" : null,
      returnRoute: isAdminSurface ? "/admin/dashboard" : isClientSurface ? "/client/dashboard" : "/memory-media-vault",
      navigationLabel: "Product Home",
      visibility: route.startsWith("/demo/") ? "HIDDEN" : "ACTIVE",
      status: "ACTIVE",
    };
  }

  if (isAny(route, AR_RECOVERY_PRODUCT_ROUTES) || route.startsWith("/admin/ar-recovery")) {
    return {
      ownerType: "PRODUCT",
      ownerName: "CollabRecover AR Solutions",
      platformName: "Business Center OS",
      workspaceName: "CollabRecover AR Solutions",
      parentRoute: "/admin/ar-recovery",
      returnRoute: "/admin/ar-recovery",
      navigationLabel: "Product Home",
      visibility: "ACTIVE",
      status: "ACTIVE",
    };
  }

  return {
    ownerType: "ROADMAP",
    ownerName: "Business Center OS",
    platformName: "Business Center OS",
    workspaceName: null,
    parentRoute: null,
    returnRoute: "/admin/business-center-os",
    navigationLabel: "Business Center OS Home",
    visibility: "ROADMAP",
    status: "ROADMAP",
  };
}

function isPublicKreatethedateProduct(route: string) {
  return [
    "/rsvp-hub",
    "/memory-hub",
    "/archive-hub",
    "/memory-media-vault",
    "/signature-song",
    "/signature-highlight-reel",
    "/partner-referral",
    "/proposal/:token",
    "/agreement/:token",
    "/payment/:token",
    "/onboarding/:token",
    "/upload-link/:uploadSlug",
    "/upload/:uploadSlug",
    "/upload/:clientSlug/:eventSlug",
    "/gallery/:clientSlug/:eventSlug",
    "/memory-hub/:clientSlug/:eventSlug",
  ].includes(route);
}

function buildRouteOwnershipEntry(route: string): RouteOwnershipEntry {
  const ownership = buildOwnership(route);
  const pageName = PAGE_NAME_OVERRIDES[route] || titleize(route === "/" ? "Forms Portal Home" : route);
  return {
    route,
    pageName,
    moduleName: pageName,
    ...ownership,
  };
}

export const ROUTE_OWNERSHIP_REGISTRY = ACTIVE_ROUTE_PATHS.map((route) => buildRouteOwnershipEntry(route));

export function getRouteOwnershipContext(pathname: string) {
  const route = normalizePath(pathname);
  return ROUTE_OWNERSHIP_REGISTRY.find((entry) => entry.route === route) ?? buildRouteOwnershipEntry(route);
}

export function getRouteInventorySummary(): RouteInventorySummary {
  const ownerTypeCounts: Record<RouteOwnerType, number> = {
    PLATFORM: 0,
    WORKSPACE: 0,
    PRODUCT: 0,
    MODULE: 0,
    ADMINISTRATION: 0,
    ROADMAP: 0,
  };
  const statusCounts: Record<RouteStatus, number> = {
    ACTIVE: 0,
    HIDDEN: 0,
    PLACEHOLDER: 0,
    ROADMAP: 0,
    DEPRECATED: 0,
  };

  for (const entry of ROUTE_OWNERSHIP_REGISTRY) {
    ownerTypeCounts[entry.ownerType] += 1;
    statusCounts[entry.status] += 1;
  }

  return {
    totalActiveRoutes: ROUTE_OWNERSHIP_REGISTRY.length,
    ownerTypeCounts,
    statusCounts,
    roadmapSlots: ROADMAP_ROUTE_SLOTS.length,
  };
}

export function buildRouteContextLine(pathname: string) {
  const context = getRouteOwnershipContext(pathname);
  const parts = [context.platformName];
  if (context.workspaceName) {
    parts.push(`Workspace: ${context.workspaceName}`);
  }
  parts.push(`Module: ${context.moduleName}`);
  return parts.join(" • ");
}
